/**
 * TanStack Query surface for the overview page — the three layers of P-02.
 *
 * One endpoint, therefore one key and one query: the whole point of the
 * contract is that the page does not fan out. Every card on the page reads the
 * same hook rather than being prop-drilled from a page-level fetch, so they
 * share one cache entry and one fetch.
 */
import { useState } from 'react'

import { queryOptions, useQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/hooks/use-derived-auth'
import { time } from '@/shared/datetime/utils/time'

import { fetchOverviewDashboard } from './api'
import { isWindowRejection } from './window-rejection'

import type { ResolvedWindow } from './window'

/**
 * Keyed by the **resolved** window, not the preset token.
 *
 * Two presets can resolve to the same bounds only by coincidence, but a preset
 * resolves to different bounds as the clock advances — and that is a different
 * answer, so it must be a different key. This is why `resolveWindow` floors
 * `now` to the minute: without it every render would mint a fresh key.
 */
export const overviewKeys = {
	all: ['air-overview'] as const,
	dashboard: (tenant: string, window: ResolvedWindow) =>
		[
			...overviewKeys.all,
			'dashboard',
			tenant,
			window.start,
			window.end,
		] as const,
}

export const overviewQueries = {
	/**
	 * The whole screen.
	 *
	 * Held for a minute: the figures are grouped aggregates over a window whose
	 * end is itself floored to the minute, so a shorter stale time would re-issue
	 * a request that cannot have changed.
	 */
	dashboard: (tenant: string, window: ResolvedWindow) =>
		queryOptions({
			queryKey: overviewKeys.dashboard(tenant, window),
			queryFn: ({ signal }) => fetchOverviewDashboard(tenant, window, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(5, 'm'),
			// Keeps the previous window's figures on screen while the next one
			// loads, so changing the range dims the page rather than blanking it.
			placeholderData: (previous) => previous,
			enabled: !!tenant,
		}),
}

/**
 * The overview payload, for everything that renders figures.
 *
 * Throws to the enclosing error boundary on failure — with one exception. A
 * rejected *window* is a user input mistake, not a page failure: it is left for
 * {@link useOverviewStatus} to render inline beside the control that caused it,
 * because a boundary would take away the only control that can fix it.
 */
export const useOverviewDashboard = (window: ResolvedWindow) => {
	const tenant = useCurrentTenant()
	return useQuery({
		...overviewQueries.dashboard(tenant, window),
		throwOnError: (error) => !isWindowRejection(error),
	})
}

/**
 * The same query, read for its *state* rather than its data — freshness, an
 * in-flight refetch, and a rejected window.
 *
 * A second hook over one `queryOptions` rather than a second query: the filter
 * bar lives outside both boundaries by design, so it cannot use a hook that
 * throws, and it needs the failure the boundary would otherwise swallow. Same
 * key, so this costs no extra request. (Precedent: the suspense/non-suspense
 * pair on `features/playbooks/queries.ts`.)
 */
export const useOverviewStatus = (window: ResolvedWindow) => {
	const tenant = useCurrentTenant()
	const { dataUpdatedAt, refetch, isFetching, error } = useQuery({
		...overviewQueries.dashboard(tenant, window),
		// **Load-bearing, and not a default.** The client sets `throwOnError: true`
		// globally (`lib/react-query.ts`), and a query that inherits it throws
		// during render — which would make the `rejection` branch below dead code
		// and, because the filter bar sits *outside* both boundaries by design,
		// send the error past them to the route's `errorComponent`. That blanks the
		// page and takes away the only control that can fix the window, which is
		// precisely what this hook exists to prevent. GOTCHAS records the same trap
		// costing the whole `/search` route.
		throwOnError: false,
	})

	return {
		dataUpdatedAt: useLastFetchedAt(dataUpdatedAt),
		refetch,
		isFetching,
		rejection: isWindowRejection(error) ? error : null,
	}
}

/**
 * The last time real data actually arrived, held across window changes.
 *
 * Query reports `dataUpdatedAt: 0` while a key is serving *placeholder* data —
 * which, on this page, is every window change, because keeping the previous
 * figures on screen is the whole point. Passed through, that epoch zero renders
 * as "over 56 years ago" beside figures that are seconds old. `0` still comes
 * back before the very first load, where there is genuinely nothing to report.
 */
function useLastFetchedAt(dataUpdatedAt: number): number {
	const [lastFetchedAt, setLastFetchedAt] = useState(dataUpdatedAt)

	// Adjusted during render rather than in an effect: this is derived state
	// with memory, and an effect would paint the wrong timestamp first (P-08).
	if (dataUpdatedAt !== 0 && dataUpdatedAt !== lastFetchedAt) {
		setLastFetchedAt(dataUpdatedAt)
	}

	return lastFetchedAt
}
